import React from 'react';
import { Table as AntTable, Space, Avatar, Popconfirm, Input, Button, Tag, Popover } from 'antd';
import { useState, useRef } from "react";
import { UserOutlined, SearchOutlined } from '@ant-design/icons';
import { genNumberTable } from '../../../../function/genNumberTable';
import { useStateContext } from '../../../../contexts/ContextProvider';
import { SHIFT_MAPPING, JOB_TYPE_MAPPING } from '../../../../data/constant';
import { AccountSecurityCell } from '../../../../components';

const headerStyle = {
  color: '#525252',
};

const Table = ({ data, onEdit, onDelete, onRemoveGuest, loading, onRefresh }) => {
  const { currentColor } = useStateContext();
  const searchInput = useRef(null);
  // เดิมเขียน `const [setSearchText] = useState('')` ซึ่ง destructure ผิดตำแหน่ง —
  // ได้ค่า '' มาเป็น setSearchText (ตัว setter อยู่ index 1) พอกดปุ่ม Search จึง
  // โยน "setSearchText is not a function" ทุกครั้ง ค่าที่เก็บก็ไม่มีใครอ่านต่อ
  // จึงตัด state ทิ้งทั้งคู่ เหลือแค่สั่ง confirm/clearFilters ให้ antd กรองให้
  const handleSearch = (selectedKeys, confirm) => {
    confirm();
  };
  const handleReset = (clearFilters, confirm) => {
    clearFilters();
    confirm();
  };
  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
      <div
        style={{
          padding: 8,
        }}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm)}
          style={{
            marginBottom: 8,
            display: 'block',
          }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm)}
            icon={<SearchOutlined />}
            size="small"
            style={{
              width: 90,
            }}
          >
            Search
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters, confirm)}
            size="small"
            style={{
              width: 90,
            }}
          >
            Reset
          </Button>

          <Button
            type="link"
            size="small"
            onClick={() => {
              close();
            }}
          >
            close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined
        style={{
          color: filtered ? '#1677ff' : undefined,
        }}
      />
    ),
    // กัน null/undefined — เดิม record[dataIndex].toString() จะ throw ถ้าค่าว่าง
    onFilter: (value, record) =>
      String(record[dataIndex] ?? '').toLowerCase().includes(String(value).toLowerCase()),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
  });

  // companies มาจาก getListUser เป็น JSON array — mysql2 แปลงเป็น object ให้แล้ว
  // แต่รับ string เผื่อไว้ด้วย (บาง driver/proxy คืนเป็นข้อความ)
  const parseCompanies = (raw) => {
    if (Array.isArray(raw)) return raw;
    if (typeof raw === 'string') {
      try { const v = JSON.parse(raw); return Array.isArray(v) ? v : []; } catch { return []; }
    }
    return [];
  };

  // กางรายละเอียดทุกสังกัด — กะ/หน้าที่เป็นของแต่ละบริษัท คนละค่ากันได้
  const companyDetail = (record) => {
    const list = parseCompanies(record.companies)
      .slice()
      .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
    const here = String(record.company_id ?? '');
    return (
      <div className="text-sm" style={{ maxWidth: 320 }}>
        {list.map((c) => (
          <div key={c.id} className="py-1 border-b last:border-b-0 border-gray-100">
            <div className="font-medium">
              {c.name}
              {String(c.id) === here && <Tag className="ml-2" color="blue">บริษัทนี้</Tag>}
              {!Number(c.enable) && <Tag className="ml-1" color="red">ปิดใช้งาน</Tag>}
            </div>
            <div className="text-gray-500">
              {JOB_TYPE_MAPPING[c.JobType] || '-'} · {SHIFT_MAPPING[c.Shift] || '-'}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const columns = [
    {
      title: 'ลำดับ',
      align: 'center',
      width: 60,
      dataIndex: 'number',
      key: 'number',
      onHeaderCell: () => { return { style: headerStyle } }
    },
    {
      title: 'UserName',
      align: 'center',
      dataIndex: 'UserName',
      key: 'UserName',
      onHeaderCell: () => { return { style: headerStyle } },
      ...getColumnSearchProps('UserName'),
    },
    {
      title: 'Shift',
      align: 'center',
      dataIndex: 'Shift',
      key: 'Shift',
      onHeaderCell: () => { return { style: headerStyle } },
      render: (shift) => SHIFT_MAPPING[shift] || '-'
    },
    {
      title: 'Role',
      align: 'center',
      dataIndex: 'JobType',
      key: 'JobType',
      onHeaderCell: () => { return { style: headerStyle } },
      render: (jobType) => JOB_TYPE_MAPPING[jobType] || '-'
    },
    {
      title: 'สถานะ',
      align: 'center',
      dataIndex: 'enable',
      key: 'enable',
      onHeaderCell: () => { return { style: headerStyle } },
      render: (enable) => (
        <Tag color={enable ? 'green' : 'red'}>
          {enable ? 'Yes' : 'No'}
        </Tag>
      )
    },
    {
      title: 'หน้าที่หลัก',
      align: 'center',
      dataIndex: 'mainTaskType',
      key: 'mainTaskType',
      onHeaderCell: () => { return { style: headerStyle } },
      render: (mainTaskType) => mainTaskType || '-'
    },
    {
      title: 'สังกัด',
      key: 'membership',
      align: 'center',
      // isGuest / homeCompanyName ไม่มีอยู่จริง — backend ไม่เคยส่งมาสักตัว
      // Number(undefined) = NaN ทุกคนจึงขึ้น "พนักงานของบริษัทนี้" และปุ่มถอนออกไม่เคยโผล่
      // ใช้ companyCount ที่ getListUser ส่งมาจริงแทน (นับสังกัดทั้งหมดของบัญชีนี้)
      // >1 = อยู่หลายบริษัท → ถอนออกจากที่นี่ได้ปลอดภัย บัญชีไม่ถูกลบ
      // =1 = ที่นี่ที่เดียว → ถอนออก = ลบบัญชี จึงให้ใช้ปุ่ม "ลบ" แทน
      render: (record) =>
        Number(record.companyCount) > 1 ? (
          <Space size="small">
            <Popover
              trigger="click"
              placement="left"
              title={`สังกัดของ ${record.UserName}`}
              content={companyDetail(record)}
            >
              <Tag color="purple" className="cursor-pointer">
                อยู่ {Number(record.companyCount)} บริษัท
              </Tag>
            </Popover>
            <Popconfirm
              title="ถอนออกจากบริษัทนี้"
              description="บัญชีไม่ถูกลบ แค่ไม่ทำงานให้บริษัทนี้อีก"
              okText="ยืนยัน"
              cancelText="ยกเลิก"
              onConfirm={() => onRemoveGuest(record)}
            >
              <span className="text-red-500 cursor-pointer">ถอนออก</span>
            </Popconfirm>
          </Space>
        ) : (
          <Tag>พนักงานของบริษัทนี้</Tag>
        ),
      onHeaderCell: () => ({ style: headerStyle }),
    },
    {
      title: 'รหัสผ่าน / 2FA',
      key: 'security',
      align: 'center',
      width: 320,
      render: (record) => (
        <AccountSecurityCell record={record} apiBase="/api/v1/users" onDone={onRefresh} />
      ),
      onHeaderCell: () => ({ style: headerStyle }),
    },
    {
      title: 'การจัดการ',
      fixed: 'right',
      key: 'actions',
      align: 'center',
      render: (record) => (
        <Space size="middle">
          <div className="text-blue-500 cursor-pointer" onClick={() => onEdit(record)}>
            แก้ไข
          </div>
          {/* คนที่อยู่หลายบริษัทไม่ต้องมีปุ่ม "ลบ" — ใช้ "ถอนออก" ในคอลัมน์สังกัดแทน
              (เส้นเดียวกันทั้งคู่: removeUserFromCompany ลบเฉพาะสังกัดนี้
               แล้วลบบัญชีให้ก็ต่อเมื่อไม่เหลือสังกัดที่ไหนเลย) */}
          {Number(record.companyCount) <= 1 && (
            <Popconfirm
              title="ลบข้อมูล"
              description="ยืนยันการลบข้อมูล ?"
              onConfirm={() => onDelete(record)}
              okText="ยืนยัน"
              cancelText="ยกเลิก"
              okButtonProps={{ style: { backgroundColor: currentColor, borderColor: currentColor } }}
            >
              <div className="text-red-500 cursor-pointer">ลบ</div>
            </Popconfirm>
          )}
        </Space>
      ),
      onHeaderCell: () => ({ style: headerStyle }),
    }
  ];

  return (
    <div className='overflow-x-auto'>
      <AntTable
        loading={loading}
        className='border-1 rounded-lg border-gray-200'
        dataSource={genNumberTable(data)}
        columns={columns}
        pagination={{ pageSize: 8 }}
        rowKey={'id'}
        scroll={{ x: 'max-content' }}
      />
    </div>
  );
};

export default Table;
